import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { ErrorBoundary, PageRenderer } from 'magical-trails-react';
import type { Page } from 'magical-trails';

import { RootState } from '../../store';
import { advanceMessagePoint, answerQuestion } from '../../store/trail';
import { compareImages } from '../../utils/compareImages';
import { trailEvent } from '../../utils/trailProgress';
import Loading from '../Loading';
import Modal from '../Modal/Modal';

interface Params {
  pointId: string;
  pageIndex: string;
}

export interface Props {
  handleClose: () => void;
}

/**
 * ONE page of a message point, full screen: the camera for a photo reply, or
 * whatever activity a card in the thread stands for. It runs through the
 * same PageRenderer as any other page; what differs is what "next" means.
 * Here, finishing the page moves the CONVERSATION on a step and drops the
 * player back in the thread, where the sender carries on.
 */
const MessagePage: React.FC<Props> = ({ handleClose: closeNow }) => {
  // ModalRouter hands over a new closure every render; PageRenderer is
  // memoised on handler identity, and a three.js page must not remount
  // because the trail behind it re-rendered.
  const closeRef = useRef(closeNow);
  closeRef.current = closeNow;
  const handleClose = useCallback(() => closeRef.current(), []);

  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { pointId, pageIndex: rawIndex } = useParams<Params>();
  const pageIndex = Number.parseInt(rawIndex, 10);
  const storyPoint = useSelector((state: RootState) =>
    state.trail.currentStory?.storyPoints.find(({ code }) => code === pointId)
  );

  // Both "continue" and "close" can fire for one visit; move on once.
  const advancedRef = useRef(false);
  const moveOn = useCallback(() => {
    if (advancedRef.current) return;
    const live = store
      .getState()
      .trail.currentStory?.storyPoints.find(({ code }) => code === pointId);
    // Only the conversation's live page moves it on: reopening a finished
    // card is just a second look.
    if (!live || live.status === 'completed') return;
    if ((live.currentPage ?? 0) !== pageIndex) return;
    advancedRef.current = true;
    dispatch(advanceMessagePoint({ code: pointId }));
  }, [store, pointId, pageIndex, dispatch]);

  const handleNext = useCallback(() => {
    moveOn();
    handleClose();
  }, [moveOn, handleClose]);

  // Closing is just closing: the page only counts as done when the player
  // goes ON from it (see `pagesWithNext` below for why that always exists).
  // Otherwise shutting the camera without taking the photo would read as
  // "photo sent".
  const handleSetPage = useCallback(
    (page: number) => {
      if (page > pageIndex) handleNext();
    },
    [pageIndex, handleNext]
  );

  const handleAnswer = useCallback(
    (payload: { code: string; page: number; userAnswer: string }) => {
      dispatch(answerQuestion(payload));
    },
    [dispatch]
  );

  const handleTrailEvent = useCallback(
    (event: string, properties?: Record<string, string | number | boolean>) =>
      trailEvent(event, store.getState(), pointId, properties),
    [store, pointId]
  );

  const noop = useCallback(() => {}, []);

  const pages = storyPoint?.pages;
  // PageRenderer gives a page no "next" when it is the point's last, and a
  // page with no next has no way to say it is finished (a skipped scan just
  // sits there). In a thread every page hands back to the conversation, so
  // each one gets somewhere to go: a blank page that is never shown, because
  // "next" closes this screen.
  const pagesWithNext = useMemo<Page[] | undefined>(
    () =>
      pages
        ? [
            // No page dots in here: there is one page, and a dot for the
            // blank one would be a second, unlabelled "next".
            ...pages.map((page) =>
              'allowNavigation' in page
                ? { ...page, allowNavigation: false }
                : page
            ),
            { type: 'pageLayout02', content: '' },
          ]
        : undefined,
    [pages]
  );

  // Nothing to show: a link with a bad page number, or a live edit removed
  // the point or the page. Go back rather than spin for ever.
  const storyLoaded = useSelector(
    (state: RootState) =>
      state.trail.trailLoaded &&
      !state.trail.loading &&
      Boolean(state.trail.currentStory)
  );
  const missing = storyLoaded && !pages?.[pageIndex];
  const closedForMissingRef = useRef(false);
  useEffect(() => {
    if (!missing || closedForMissingRef.current) return;
    closedForMissingRef.current = true;
    handleClose();
  }, [missing, handleClose]);

  return (
    <Modal isOpen handleClose={handleClose} type="fullScreen">
      {pages && pages[pageIndex] ? (
        <ErrorBoundary
          key={`${pointId}:${pageIndex}`}
          fallback={
            <div
              role="alert"
              className="flex h-full w-full flex-col items-center justify-center bg-black p-6 text-center"
            >
              <p className="text-sm font-semibold text-white">
                This page hit a snag
              </p>
              <button
                type="button"
                onClick={handleNext}
                className="mt-btn bg-mt-primary text-mt-on-primary mt-4 rounded-md px-4 py-2 text-sm font-semibold"
              >
                Back to the conversation
              </button>
            </div>
          }
        >
          <PageRenderer
            pages={pagesWithNext as Page[]}
            code={pointId}
            currentPage={pageIndex}
            answerQuestion={handleAnswer}
            handlenextPage={handleNext}
            handleprevPage={noop}
            handleClose={handleClose}
            handleSetPage={handleSetPage}
            trailEvent={handleTrailEvent}
            compareImages={compareImages}
          />
        </ErrorBoundary>
      ) : (
        <Loading />
      )}
    </Modal>
  );
};

export default MessagePage;
